/**
 * Country flag utility — converts country names to Unicode flag emojis.
 * Flags are rendered natively by browsers using Regional Indicator Symbols.
 * Zero storage cost, zero API calls.
 */

// Common country name → ISO 3166-1 alpha-2 code mapping
const COUNTRY_TO_ISO = {
  "Afghanistan": "AF", "Albania": "AL", "Algeria": "DZ", "Argentina": "AR",
  "Armenia": "AM", "Australia": "AU", "Austria": "AT", "Azerbaijan": "AZ",
  "Bahamas": "BS", "Bahrain": "BH", "Bangladesh": "BD", "Belgium": "BE",
  "Belize": "BZ", "Bolivia": "BO", "Bosnia and Herzegovina": "BA",
  "Brazil": "BR", "Brunei": "BN", "Bulgaria": "BG", "Cambodia": "KH",
  "Canada": "CA", "Chile": "CL", "China": "CN", "Colombia": "CO",
  "Costa Rica": "CR", "Croatia": "HR", "Cuba": "CU", "Cyprus": "CY",
  "Czech Republic": "CZ", "Czechia": "CZ", "Denmark": "DK",
  "Dominican Republic": "DO", "Ecuador": "EC", "Egypt": "EG",
  "El Salvador": "SV", "Estonia": "EE", "Ethiopia": "ET",
  "Fiji": "FJ", "Finland": "FI", "France": "FR", "French Polynesia": "PF",
  "Georgia": "GE", "Germany": "DE", "Ghana": "GH", "Greece": "GR",
  "Guatemala": "GT", "Honduras": "HN", "Hong Kong": "HK", "Hungary": "HU",
  "Iceland": "IS", "India": "IN", "Indonesia": "ID", "Iran": "IR",
  "Iraq": "IQ", "Ireland": "IE", "Israel": "IL", "Italy": "IT",
  "Jamaica": "JM", "Japan": "JP", "Jordan": "JO", "Kazakhstan": "KZ",
  "Kenya": "KE", "Kuwait": "KW", "Laos": "LA", "Latvia": "LV",
  "Lebanon": "LB", "Lithuania": "LT", "Luxembourg": "LU", "Macau": "MO",
  "Malaysia": "MY", "Maldives": "MV", "Malta": "MT", "Mexico": "MX",
  "Moldova": "MD", "Monaco": "MC", "Mongolia": "MN", "Montenegro": "ME",
  "Morocco": "MA", "Myanmar": "MM", "Nepal": "NP", "Netherlands": "NL",
  "New Zealand": "NZ", "Nicaragua": "NI", "Nigeria": "NG", "North Korea": "KP",
  "North Macedonia": "MK", "Norway": "NO", "Oman": "OM", "Pakistan": "PK",
  "Panama": "PA", "Paraguay": "PY", "Peru": "PE", "Philippines": "PH",
  "Poland": "PL", "Portugal": "PT", "Qatar": "QA", "Romania": "RO",
  "Russia": "RU", "Saudi Arabia": "SA", "Serbia": "RS", "Singapore": "SG",
  "Slovakia": "SK", "Slovenia": "SI", "South Africa": "ZA",
  "South Korea": "KR", "Spain": "ES", "Sri Lanka": "LK", "Sweden": "SE",
  "Switzerland": "CH", "Taiwan": "TW", "Tanzania": "TZ", "Thailand": "TH",
  "Tunisia": "TN", "Turkey": "TR", "UAE": "AE",
  "United Arab Emirates": "AE", "Uganda": "UG", "Ukraine": "UA",
  "United Kingdom": "GB", "United States": "US", "Uruguay": "UY",
  "Uzbekistan": "UZ", "Venezuela": "VE", "Vietnam": "VN",
  // Aliases
  "Korea": "KR", "Korea, Republic of": "KR", "Türkiye": "TR",
  "Viet Nam": "VN", "UK": "GB", "USA": "US", "U.S.": "US",
  "Republic of Korea": "KR",
};

/**
 * Convert a 2-letter ISO code to a flag emoji using Regional Indicator Symbols.
 */
const isoToFlag = (iso) => {
  if (!iso || iso.length !== 2) return '';
  const upper = iso.toUpperCase();
  const cp1 = 0x1F1E6 + upper.charCodeAt(0) - 65;
  const cp2 = 0x1F1E6 + upper.charCodeAt(1) - 65;
  return String.fromCodePoint(cp1, cp2);
};

/**
 * Get the flag emoji for a country name.
 * Returns the flag + a space, or empty string if not found.
 * Usage: `${getCountryFlag("Japan")}Japan` → "🇯🇵 Japan"
 */
export const getCountryFlag = (countryName) => {
  if (!countryName) return '';
  const iso = COUNTRY_TO_ISO[countryName];
  if (iso) return isoToFlag(iso);
  // Try case-insensitive lookup
  const lower = countryName.toLowerCase();
  for (const [name, code] of Object.entries(COUNTRY_TO_ISO)) {
    if (name.toLowerCase() === lower) return isoToFlag(code);
  }
  return '';
};

/**
 * Format a country name with its flag prefix.
 * Example: flagCountry("Japan") → "🇯🇵 Japan"
 */
export const flagCountry = (countryName) => {
  if (!countryName) return countryName;
  const flag = getCountryFlag(countryName);
  return flag ? `${flag} ${countryName}` : countryName;
};
